import React, { useEffect, useState } from 'react';
import { type Control, Controller, type FieldErrors, type UseFormSetValue } from 'react-hook-form';
import gtm from '@periodica/analytics/gtm';
import * as Radio from '@periodica/ui-kit/radio';
import { ControlLabel, FormField, PhoneField, TextField } from '@periodica/ui-kit';
import { Switcher } from '@components/Blocks/Switcher';
import { FormBlockHeading } from './FormBlockHeading';
import {
  DELIVERY_WAY_EMAIL,
  DELIVERY_WAY_SMS,
  TYPE_RECIPIENT_ME,
  TYPE_RECIPIENT_PERSON,
} from '../consts';
import { validatePhoneNumber } from '../validate';
import type { GiftFormValues } from '../types';

import styles from './GiftForm.module.scss';

const radioDeliveryWayOptions = [
  {
    id: DELIVERY_WAY_SMS,
    label: 'SMS на телефон',
    description: 'Отправим ссылку на сертификат в SMS',
  },
  {
    id: DELIVERY_WAY_EMAIL,
    label: 'На E-mail',
    description: 'Отправим сертификат на электронную почту',
  },
];

const switcherDeliveryOptions = [
  {
    id: TYPE_RECIPIENT_PERSON,
    title: 'Получателю',
  },
  {
    id: TYPE_RECIPIENT_ME,
    title: 'Себе',
  },
];

type GiftDeliveryStepProps = {
  control: Control<GiftFormValues>;
  errors: FieldErrors<GiftFormValues>;
  setValue: UseFormSetValue<GiftFormValues>;
  deliveryRecipient: GiftFormValues['deliveryRecipient'];
  deliveryWay: GiftFormValues['deliveryWay'];
};

export function GiftDeliveryStep({
  control,
  errors,
  setValue,
  deliveryWay,
  deliveryRecipient,
}: GiftDeliveryStepProps) {
  const [previosValues, setPreviousValues] = useState<{
    emailTo: GiftFormValues['emailTo'];
    phoneTo: GiftFormValues['phoneTo'];
  }>({ emailTo: '', phoneTo: '' });

  useEffect(() => {
    if (deliveryRecipient === TYPE_RECIPIENT_PERSON) {
      if (deliveryWay === DELIVERY_WAY_EMAIL && previosValues.emailTo) {
        setValue('emailTo', previosValues.emailTo);
      }

      if (deliveryWay === DELIVERY_WAY_SMS && previosValues.phoneTo) {
        setValue('phoneTo', previosValues.phoneTo);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deliveryWay, deliveryRecipient]);

  return (
    <div className={styles.formBlock}>
      <FormBlockHeading>5. Куда отправить сертификат</FormBlockHeading>
      <div className={styles.switcher}>
        <Controller
          render={({ field: { onChange, value } }) => (
            <Switcher
              activeOption={value}
              options={switcherDeliveryOptions}
              onSwitch={(type) => {
                gtm.logEvent({ name: `e_site_gift_choose_recipient_${type}` });
                onChange(type);
              }}
            />
          )}
          control={control}
          name="deliveryRecipient"
        />
      </div>
      <Controller
        render={({ field: { onChange, value } }) => (
          <Radio.Provider
            value={value}
            setValue={(way) => {
              gtm.logEvent({ name: `e_site_gift_choose_way_${way}` });
              onChange(way);
            }}
          >
            <Radio.Group render={<div className={styles.radioGroup} />}>
              {radioDeliveryWayOptions.map((way) => (
                <ControlLabel key={way.id} {...way}>
                  <Radio.Control value={way.id} slot="radio" />
                </ControlLabel>
              ))}
            </Radio.Group>
          </Radio.Provider>
        )}
        control={control}
        name="deliveryWay"
      />

      {deliveryRecipient === TYPE_RECIPIENT_PERSON &&
        (deliveryWay === DELIVERY_WAY_EMAIL ? (
          <Controller
            render={({ field: { onChange, value, ref } }) => (
              <FormField
                name="emailTo"
                label="E-mail получателя"
                error={errors && errors.emailTo && errors.emailTo.message}
              >
                <TextField
                  ref={ref}
                  name="emailTo"
                  error={errors && errors.emailTo && errors.emailTo.message}
                  onChange={(val) => onChange(val)}
                  onBlur={() => {
                    if (value && !(errors && errors.emailTo && errors.emailTo.message)) {
                      setPreviousValues((prev) => ({ ...prev, emailTo: value }));
                      gtm.logEvent({ name: 'event_site_gift_enter-mail-to' });
                    }
                  }}
                  value={value}
                  type="email"
                />
              </FormField>
            )}
            key="emailTo"
            control={control}
            name="emailTo"
            rules={{
              required: 'Введите E-mail получателя',
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: 'Введите E-mail в формате name@domain.com',
              },
            }}
          />
        ) : (
          <Controller
            render={({ field: { onChange, value, ref } }) => (
              <FormField
                name="phoneTo"
                label="Телефон получателя"
                error={errors && errors.phoneTo && errors.phoneTo.message}
              >
                <PhoneField
                  ref={ref}
                  value={value}
                  onChange={onChange}
                  onBlur={() => {
                    if (value && !(errors && errors.phoneTo && errors.phoneTo.message)) {
                      setPreviousValues((prev) => ({ ...prev, phoneTo: value }));
                      gtm.logEvent({ name: 'event_site_gift_enter-phone-to' });
                    }
                  }}
                  name="phoneTo"
                  type="text"
                  placeholder="+7 (___) ___-__-__"
                  error={errors && errors.phoneTo && errors.phoneTo.message}
                />
              </FormField>
            )}
            key="phoneTo"
            control={control}
            name="phoneTo"
            rules={{
              required: 'Введите телефон получателя',
              validate: (value) => validatePhoneNumber(value),
            }}
          />
        ))}
    </div>
  );
}
